import type { FlowGraphEdgeV1, FlowGraphNodeV1, FlowGraphV1 } from './graph'

export type NodeData = Record<string, unknown> | undefined
export type Registers = Record<string, number>

/** Output ports of the "Ask: known?" action; every other action leaves through 'next'. */
export const ASK_KNOWN_PORTS = ['known', 'unknown']

/** Ports of an action node that has more than the default 'next' output, else null. */
export const actionOutputPorts = (data: NodeData): string[] | null =>
  data?.actionType === 'ask-known' ? ASK_KNOWN_PORTS : null

export function readNodeString(data: NodeData, key: string): string | undefined {
  const value = data?.[key]
  return typeof value === 'string' ? value.trim() || undefined : undefined
}

/** Preserve stored edge order: it determines the legacy first-edge fallback. */
export function indexGraph(graph: FlowGraphV1 | null) {
  const nodes = new Map<string, FlowGraphNodeV1>()
  const outgoing = new Map<string, FlowGraphEdgeV1[]>()
  for (const node of graph?.nodes ?? []) nodes.set(node.id, node)
  for (const edge of graph?.edges ?? []) {
    const edges = outgoing.get(edge.fromNodeId) ?? []
    edges.push(edge)
    outgoing.set(edge.fromNodeId, edges)
  }
  return { nodes, outgoing }
}

export function nextNodeId(
  edges: FlowGraphEdgeV1[],
  preferredPorts: string[],
  strict = false,
): string | null {
  for (const port of preferredPorts) {
    const edge = edges.find((candidate) => candidate.fromPort === port)
    if (edge) return edge.toNodeId
  }
  return strict ? null : (edges[0]?.toNodeId ?? null)
}

export function evaluateCondition(data: NodeData, registers: Registers): boolean {
  const operand = (side: string, field: string) => {
    if (side === 'R1' || side === 'R2' || side === 'R3') return registers[side] ?? 0
    const value = data?.[field]
    return typeof value === 'number' ? value : 0
  }
  const left = operand(readNodeString(data, 'condLeft') ?? 'R1', 'condLeftLit')
  const right = operand(readNodeString(data, 'condRight') ?? 'literal', 'condRightLit')
  switch (readNodeString(data, 'condOp') ?? '==') {
    case '==':
      return left === right
    case '!=':
      return left !== right
    case '<':
      return left < right
    case '<=':
      return left <= right
    case '>':
      return left > right
    case '>=':
      return left >= right
    default:
      return false
  }
}

/** Older stored graphs encode the audio index and waiting policy in delayMs. */
export function decodeAudioAction(data: NodeData) {
  const delay = data?.delayMs
  const index =
    typeof data?.audioElementIndex === 'number'
      ? data.audioElementIndex
      : typeof data?.audioIndex === 'number'
        ? data.audioIndex
        : typeof delay === 'number'
          ? delay < 0
            ? -(delay + 1)
            : Math.max(0, Math.floor(delay))
          : 0
  const waitForAudio =
    typeof data?.waitForAudio === 'boolean'
      ? data.waitForAudio
      : typeof delay === 'number'
        ? delay >= 0
        : true
  return { index, waitForAudio }
}
